import { useGraphApi } from '@squareup/dex-feature-hooks-graph';
import {
  ApiAside,
  ReferencePage,
  ReferenceItem,
  ReferenceSubItemProps,
} from '@squareup/dex-tech-ui';
import {
  ReferenceHrefBuilders,
  ReferencePath,
  toUrl,
} from '@squareup/dex-tech-utils-routing';
import { HeadWrapper } from '@squareup/dex-ui';
import { Markdown } from '@squareup/dex-ui-markdown';
import { Box, Paragraph20 } from '@squareup/dex-ui-shared-base';
import { MarketEmptyState } from '@squareup/dex-ui-shared-market';
import {
  getNextPublicApiReferenceNameOrDefault,
  getNextPublicSquareDocsURL,
} from '@squareup/dex-utils-environment';
import {
  filterForValidGraphObjects,
  toApiId,
  toWebhookPathParam,
} from '@squareup/dex-utils-oas';
import { useParams } from '@squareup/dex-utils-shared-routing-hooks';
import { humanizeName } from '@squareup/dex-utils-text';
import clsx from 'clsx';
import React, { ReactNode, useMemo, useRef } from 'react';

import { SquareVersionPage } from '../../../../../shared/SquareVersionPage';

import styles from './index.module.css';

const strings = {
  noSupportedWebhooksExistMessage:
    'This API does not support webhooks in the current version.',
};

const FilteredApiWebhooks = ({ version }: { version: string }) => {
  const { api } = useGraphApi();

  let apiWebhooks: ReferenceSubItemProps[] = [];

  if (api) {
    const { webhooks, id } = api;
    apiWebhooks = filterForValidGraphObjects(webhooks).map((webhook) => ({
      item: {
        // `webhook` contains getters which cannot be spread, so instead,
        // required properties are explicitly specified below:
        description: webhook?.description?.split('. ', 1)[0],
        isBeta: webhook.isBeta,
        isDeprecated: webhook.isDeprecated,
        isV1: webhook.isV1,
        title: humanizeName(webhook.name),
      },
      href: ReferenceHrefBuilders.versionApiWebhook({
        square_version: version,
        api: `${id}-api`,
        webhook: toWebhookPathParam(webhook),
      }),
      key: webhook.id,
    }));
  }

  if (apiWebhooks.length === 0) {
    return (
      <MarketEmptyState className={clsx(styles['api-webhooks-empty-state'])}>
        <Paragraph20 slot="primary-text">
          {strings.noSupportedWebhooksExistMessage}
        </Paragraph20>
      </MarketEmptyState>
    );
  }

  return <ReferenceItem key="webhooks" subItems={apiWebhooks} />;
};

const PageContent = () => {
  const { api, error } = useGraphApi();
  const aside = useRef<ApiAside>({
    trackingId: 'unknown',
    content: null,
  });
  const query = useParams();
  const squareVersion = query?.square_version as string;

  const description = api?.summary ?? '';
  const title = api
    ? `Webhooks - ${api.name} API - ${getNextPublicApiReferenceNameOrDefault()}`
    : getNextPublicApiReferenceNameOrDefault();

  const backHref = useMemo(() => {
    return ReferenceHrefBuilders.versionApi({
      square_version: squareVersion,
      api: toApiId(api?.id),
    });
  }, [squareVersion, api]);

  if (!api || error) {
    return null;
  }

  aside.current = {
    trackingId: `square-version-api-${api.id}-endpoints-webhooks`,
    content: <FilteredApiWebhooks version={squareVersion} />,
    navigationItems: [
      {
        trackingId: `navigate-to-reference-api-${api.id}-endpoints`,
        href: {
          pathname: ReferencePath.VERSION_API,
          query,
        },
        isActive: false,
        testId: 'endpoints',
        text: 'Endpoints',
      },
      {
        trackingId: `navigate-to-reference-api-${api.id}-webhooks`,

        href: {
          pathname: ReferencePath.VERSION_API_WEBHOOK_INDEX,
          query,
        },
        isActive: true,
        testId: 'webhooks',
        text: 'Webhooks',
      },
    ],
  };

  return (
    <>
      <HeadWrapper
        description={description}
        preconnectUrl={getNextPublicSquareDocsURL()}
        title={title}
        canonicalPath={toUrl(
          ReferenceHrefBuilders.versionApiWebhookIndex({
            square_version: squareVersion,
            api: `${api.id}-api`,
          })
        )}
      />
      <ReferencePage
        eyebrow={'Square API'}
        title={api?.name}
        subTitle={api?.summary}
        description={
          <Box margin={{ bottom: '2x' }}>
            {api.description && (
              <Markdown markdown={api.description}></Markdown>
            )}
          </Box>
        }
        backHref={backHref}
        aside={aside.current}
      />
    </>
  );
};

class ReferenceWebhookApiPage extends SquareVersionPage {
  public static override routeHandlerName = '[square_version]/[api]/webhooks';

  protected override getPageContent(): ReactNode {
    return super.getPageContent({
      children: <PageContent />,
    });
  }
}

export default ReferenceWebhookApiPage;
